import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/base/SEO';

export default function SolutionsPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://braincommerce.com';
  const [activeTab, setActiveTab] = useState<'hr' | 'ceo' | 'industry'>('hr');

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Solutions",
    "description": "Tailored hybrid workforce solutions for HR leaders, CEOs, and industry operations.",
    "url": `${siteUrl}/solutions`
  };

  return (
    <>
      <SEO
        title="Hybrid Workforce Solutions for HR, CEOs & Industries | BrainCommerce"
        description="Discover tailored hybrid workforce solutions for HR leaders, CEOs, and industry operations. Case studies showing proven results in workforce optimization and automation."
        keywords="workforce solutions, HR solutions, CEO solutions, industry automation, hybrid workforce, case studies, workforce optimization"
        canonical={`${siteUrl}/solutions`}
        schema={schema}
      />
      
      <div className="min-h-screen bg-white pt-16 md:pt-20">
        {/* Hero Section */}
        <section className="py-12 md:py-20 px-4 md:px-8 lg:px-32 bg-gradient-to-b from-[#fafafa] to-white">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-extralight text-[#1a1a1a] mb-6 md:mb-8 tracking-tight">
              {t('nav.solutions')}
            </h1>
          </div>
        </section>

        {/* Tabs */}
        <section className="px-4 md:px-8 lg:px-32 mb-8 md:mb-12">
          <div className="flex flex-col sm:flex-row justify-center gap-2 md:gap-4">
            <button
              onClick={() => setActiveTab('hr')}
              className={`px-4 md:px-8 py-2 md:py-3 rounded-full transition-all whitespace-nowrap cursor-pointer text-sm md:text-base ${
                activeTab === 'hr'
                  ? 'bg-[#1a1a1a] text-white'
                  : 'bg-white text-[#1a1a1a] border border-gray-300 hover:border-[#1a1a1a]'
              }`}
            >
              {t('solutions.tabs.hr')}
            </button>
            <button
              onClick={() => setActiveTab('ceo')}
              className={`px-4 md:px-8 py-2 md:py-3 rounded-full transition-all whitespace-nowrap cursor-pointer text-sm md:text-base ${
                activeTab === 'ceo'
                  ? 'bg-[#1a1a1a] text-white'
                  : 'bg-white text-[#1a1a1a] border border-gray-300 hover:border-[#1a1a1a]'
              }`}
            >
              {t('solutions.tabs.ceo')}
            </button>
            <button
              onClick={() => setActiveTab('industry')}
              className={`px-4 md:px-8 py-2 md:py-3 rounded-full transition-all whitespace-nowrap cursor-pointer text-sm md:text-base ${
                activeTab === 'industry'
                  ? 'bg-[#1a1a1a] text-white'
                  : 'bg-white text-[#1a1a1a] border border-gray-300 hover:border-[#1a1a1a]'
              }`}
            >
              {t('solutions.tabs.industry')}
            </button>
          </div>
        </section>

        {/* Content */}
        <section className="px-4 md:px-8 lg:px-32 pb-12 md:pb-20">
          {activeTab === 'hr' && (
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-light text-[#1a1a1a] mb-6 md:mb-8 text-center tracking-tight">
                {t('solutions.hr.challenge')}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
                <div className="bg-[#fafafa] rounded-lg p-4 md:p-6">
                  <i className="ri-layout-grid-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                  <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.hr.benefit1')}</h3>
                </div>
                <div className="bg-[#fafafa] rounded-lg p-4 md:p-6">
                  <i className="ri-team-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                  <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.hr.benefit2')}</h3>
                </div>
                <div className="bg-[#fafafa] rounded-lg p-4 md:p-6">
                  <i className="ri-lightbulb-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                  <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.hr.benefit3')}</h3>
                </div>
              </div>

              <div className="space-y-4 md:space-y-6">
                <div className="bg-white rounded-lg p-4 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                  <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0">
                      <div className="text-3xl md:text-5xl font-extralight text-[#1a1a1a]">{t('solutions.hr.case1.metric')}</div>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.hr.case1.company')}</h4>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>과제:</strong> {t('solutions.hr.case1.challenge')}</p>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>솔루션:</strong> {t('solutions.hr.case1.solution')}</p>
                      <p className="text-sm md:text-base text-[#1a1a1a] font-semibold"><strong>결과:</strong> {t('solutions.hr.case1.result')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                  <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0">
                      <div className="text-3xl md:text-5xl font-extralight text-[#1a1a1a]">{t('solutions.hr.case2.metric')}</div>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.hr.case2.company')}</h4>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>과제:</strong> {t('solutions.hr.case2.challenge')}</p>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>솔루션:</strong> {t('solutions.hr.case2.solution')}</p>
                      <p className="text-sm md:text-base text-[#1a1a1a] font-semibold"><strong>결과:</strong> {t('solutions.hr.case2.result')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                  <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0">
                      <div className="text-3xl md:text-5xl font-extralight text-[#1a1a1a]">{t('solutions.hr.case3.metric')}</div>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.hr.case3.company')}</h4>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>과제:</strong> {t('solutions.hr.case3.challenge')}</p>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>솔루션:</strong> {t('solutions.hr.case3.solution')}</p>
                      <p className="text-sm md:text-base text-[#1a1a1a] font-semibold"><strong>결과:</strong> {t('solutions.hr.case3.result')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ceo' && (
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-light text-[#1a1a1a] mb-6 md:mb-8 text-center tracking-tight">
                {t('solutions.ceo.challenge')}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
                <div className="bg-[#fafafa] rounded-lg p-4 md:p-6">
                  <i className="ri-line-chart-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                  <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.ceo.benefit1')}</h3>
                </div>
                <div className="bg-[#fafafa] rounded-lg p-4 md:p-6">
                  <i className="ri-money-dollar-circle-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                  <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.ceo.benefit2')}</h3>
                </div>
                <div className="bg-[#fafafa] rounded-lg p-4 md:p-6">
                  <i className="ri-trophy-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                  <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.ceo.benefit3')}</h3>
                </div>
              </div>

              <div className="space-y-4 md:space-y-6">
                <div className="bg-white rounded-lg p-4 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                  <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0">
                      <div className="text-3xl md:text-5xl font-extralight text-[#1a1a1a]">{t('solutions.ceo.case1.metric')}</div>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.ceo.case1.company')}</h4>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>과제:</strong> {t('solutions.ceo.case1.challenge')}</p>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>솔루션:</strong> {t('solutions.ceo.case1.solution')}</p>
                      <p className="text-sm md:text-base text-[#1a1a1a] font-semibold"><strong>결과:</strong> {t('solutions.ceo.case1.result')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                  <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0">
                      <div className="text-3xl md:text-5xl font-extralight text-[#1a1a1a]">{t('solutions.ceo.case2.metric')}</div>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.ceo.case2.company')}</h4>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>과제:</strong> {t('solutions.ceo.case2.challenge')}</p>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>솔루션:</strong> {t('solutions.ceo.case2.solution')}</p>
                      <p className="text-sm md:text-base text-[#1a1a1a] font-semibold"><strong>결과:</strong> {t('solutions.ceo.case2.result')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                  <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0">
                      <div className="text-3xl md:text-5xl font-extralight text-[#1a1a1a]">{t('solutions.ceo.case3.metric')}</div>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.ceo.case3.company')}</h4>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>과제:</strong> {t('solutions.ceo.case3.challenge')}</p>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>솔루션:</strong> {t('solutions.ceo.case3.solution')}</p>
                      <p className="text-sm md:text-base text-[#1a1a1a] font-semibold"><strong>결과:</strong> {t('solutions.ceo.case3.result')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'industry' && (
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-light text-[#1a1a1a] mb-6 md:mb-8 text-center tracking-tight">
                {t('solutions.industry.challenge')}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12">
                <div className="bg-[#fafafa] rounded-lg p-4 md:p-6">
                  <i className="ri-truck-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                  <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.industry.benefit1')}</h3>
                </div>
                <div className="bg-[#fafafa] rounded-lg p-4 md:p-6">
                  <i className="ri-settings-3-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                  <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.industry.benefit2')}</h3>
                </div>
                <div className="bg-[#fafafa] rounded-lg p-4 md:p-6">
                  <i className="ri-customer-service-2-line text-2xl md:text-3xl text-[#1a1a1a] mb-3 md:mb-4"></i>
                  <h3 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.industry.benefit3')}</h3>
                </div>
              </div>

              <div className="space-y-4 md:space-y-6">
                <div className="bg-white rounded-lg p-4 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                  <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0">
                      <div className="text-3xl md:text-5xl font-extralight text-[#1a1a1a]">{t('solutions.industry.case1.metric')}</div>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.industry.case1.company')}</h4>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>과제:</strong> {t('solutions.industry.case1.challenge')}</p>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>솔루션:</strong> {t('solutions.industry.case1.solution')}</p>
                      <p className="text-sm md:text-base text-[#1a1a1a] font-semibold"><strong>결과:</strong> {t('solutions.industry.case1.result')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                  <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0">
                      <div className="text-3xl md:text-5xl font-extralight text-[#1a1a1a]">{t('solutions.industry.case2.metric')}</div>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.industry.case2.company')}</h4>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>과제:</strong> {t('solutions.industry.case2.challenge')}</p>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>솔루션:</strong> {t('solutions.industry.case2.solution')}</p>
                      <p className="text-sm md:text-base text-[#1a1a1a] font-semibold"><strong>결과:</strong> {t('solutions.industry.case2.result')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                  <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                    <div className="flex-shrink-0">
                      <div className="text-3xl md:text-5xl font-extralight text-[#1a1a1a]">{t('solutions.industry.case3.metric')}</div>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-base md:text-lg font-semibold text-[#1a1a1a] mb-2">{t('solutions.industry.case3.company')}</h4>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>과제:</strong> {t('solutions.industry.case3.challenge')}</p>
                      <p className="text-sm md:text-base text-[#4a4a4a] mb-2"><strong>솔루션:</strong> {t('solutions.industry.case3.solution')}</p>
                      <p className="text-sm md:text-base text-[#1a1a1a] font-semibold"><strong>결과:</strong> {t('solutions.industry.case3.result')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="text-center mt-12 md:mt-16">
            <Link 
              to="/contact"
              className="inline-flex items-center gap-3 px-8 md:px-12 py-3 md:py-4 bg-[#1a1a1a] text-white rounded-full hover:bg-[#2a2a2a] transition-all whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              {t('solutions.contactCta')}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
